import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '@common-web/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-sm hover:bg-destructive/90',
        outline: 'border border-input hover:bg-accent hover:text-accent-foreground',
        outlineDark: 'border border-input hover:bg-accent hover:text-accent-foreground text-white',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 hover:shadow-md',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // New gradient variants
        'gradient-primary':
          'bg-gradient-to-r from-primary-blue to-primary-blue-light text-white shadow-sm hover:shadow-glow-primary',
        'gradient-vibrant':
          'bg-gradient-to-r from-indigo to-teal text-white shadow-sm',
        'gradient-success':
          'bg-gradient-to-r from-emerald to-teal text-white shadow-sm',
        'gradient-danger':
          'bg-gradient-to-r from-rose to-red-500 text-white shadow-sm',
        // Glass morphism variant
        glass:
          'backdrop-blur-md bg-white/20 border border-white/30 text-primary-foreground shadow-sm hover:bg-white/30 hover:shadow-md',
      },
      size: {
        default: 'h-10 px-5 py-2.5',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-lg px-8 text-base',
        icon: 'h-10 w-10',
      },
      // New loading state
      loading: {
        true: 'relative text-transparent hover:text-transparent cursor-wait',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      loading: false,
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => any | Promise<any>;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading: loadingProp = false,
      leftIcon,
      rightIcon,
      children,
      onClick,
      ...props
    },
    ref,
  ) => {
    const [isAsyncLoading, setIsAsyncLoading] = React.useState(false);
    const loading = loadingProp || isAsyncLoading;

    const handleClick = React.useCallback(
      async (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!onClick) return;

        const result = onClick(event);
        if (result instanceof Promise) {
          setIsAsyncLoading(true);
          try {
            await result;
          } finally {
            setIsAsyncLoading(false);
          }
        }
      },
      [onClick],
    );

    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({variant, size, loading, className}))}
        ref={ref}
        disabled={props.disabled || loading}
        onClick={handleClick}
        {...props}
      >
        {loading ? (
          <div className='absolute inset-0 flex items-center justify-center'>
            <svg
              className='animate-spin h-4 w-4 text-current'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
          </div>
        ) : (
          children
        )}
        {/*{leftIcon && !loading && <span className='mr-2'>{leftIcon}</span>}*/}
        {/*{rightIcon && !loading && <span className='ml-2'>{rightIcon}</span>}*/}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export {Button, buttonVariants};
